import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
    persistReducer,
    persistStore,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/es/storage';
import { useDispatch } from 'react-redux';
import logger from 'redux-logger';
import { logDev } from '../utils/loggerHelpers';
import dialerReducer, {dialerPersistConfig} from './features/calls/dialerSlice';
import toastReducer from './features/Notifications/notificationsSlice';
import filtersReducer from './features/Filters/filtersSlice';
import authReducer from './features/Auth/authSlice';
import {authPersistBlacklist, migrateAuthState} from './features/Auth/authPersistence';
import missedCallsSlice from './features/MissedCalls/missedCallsSlice';
import idResolverReducer from './features/IdResolver/idResolverSlice';
import statusSubscriptionReducer from './features/StatusSubscription/statusSubscriptionSlice';
import  {callMetadataReducer} from './features/MatrixEvents/MatrixEventsService';
import api from "./api.ts";
import {audioMiddleware} from "./Dialer/audioMiddleware.ts";
import callSurfaceReducer from '../features/callSurface/store/callSurfaceSlice';
import callChangesReducer from './features/calls/callChangesSlice';

const dialerConfig = {
    key: 'dialer',
    blacklist: dialerPersistConfig.blacklist,
    storage,
};
const authConfig = {
    key: 'auth',
    blacklist: authPersistBlacklist,
    migrate: migrateAuthState,
    storage,
};
const persistedDialerReducer = persistReducer(dialerConfig, dialerReducer);
const persistedAuthReducer = persistReducer(authConfig, authReducer);
const reducers = combineReducers({
    [api.reducerPath]: api.reducer,
    auth: persistedAuthReducer,
    dialer: persistedDialerReducer,
    missedCalls: missedCallsSlice,
    idResolver: idResolverReducer,
    statusSubscription: statusSubscriptionReducer,

    toast: toastReducer,
    filters: filtersReducer,
    callMetadataSlice: callMetadataReducer,
    callSurface: callSurfaceReducer,
    callChanges: callChangesReducer,
});

// Define RootState from the reducers, not from store.getState()
export type RootState = ReturnType<typeof reducers>;

const isLoggerEnabled = false;

const store = configureStore({
    reducer: reducers,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        })
            .concat(api.middleware)
            .concat(audioMiddleware)
            .concat(isLoggerEnabled ? [logger] : []),
});

const persistor = persistStore(store, null, () => {
    const currentState = store.getState();
    logDev('Redux rehydration completed', {
        source: 'redux',
        event: 'rehydration_complete',
        dialerState: JSON.stringify(currentState.dialer)
    });
});

setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;

export { store, persistor };